import httpClient from '@/lib/httpClient'
import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

export type FeVisibility = 'lock' | 'hide'

/** How a locked feature is presented — from the API, never from plan names. */
export type FeatureBadge = 'coming_soon' | 'upgrade'

export interface PlanFeature {
  lookup_key: string
  name: string
  available: boolean
  value: string | null
  fe_visibility: FeVisibility
  upsell_modal: boolean
  /** basic | pro */
  plan: string
  /** null while available */
  badge: FeatureBadge | null
}

export type EarlyAccessStatus = 'requested' | 'approved' | 'declined'

export type BillingInterval = 'month' | 'year'

export interface SubscriptionDetails {
  plan_key: string | null
  /** basic | pro; null for legacy plans */
  tier: string | null
  plan_name: string
  status: string
  interval: BillingInterval | null
  /** cents */
  amount: number | null
  currency: string | null
  is_trialing: boolean
  trial_ends_at: string | null
  trial_days_left: number | null
  current_period_end: string | null
  /** null while cancelling */
  next_billing_date: string | null
  cancel_at_period_end: boolean
  /** effective cancellation date, when one is scheduled */
  cancel_at: string | null
  /** past_due: still has access, but the card needs fixing */
  payment_issue: boolean
  pay_invoice_url: string | null
}

export interface BillingSubscription {
  has_active_subscription: boolean
  /** Only the space owner can pay, change plan or cancel. */
  is_billing_owner: boolean
  team_type?: string
  subscription: SubscriptionDetails | null
  /**
   * The subscription ended for good (cancelled / trial expired). Stripe never
   * re-activates it, so a new plan must be chosen.
   */
  ended_subscription?: {
    status: string
    plan_name: string
    was_trial: boolean
    ended_at: string | null
  } | null
  features: PlanFeature[]
  /** Pro availability switch (PRO_AVAILABILITY on the backend). */
  pro: { availability: 'coming_soon' | 'available'; purchasable: boolean }
  /** The signed-in user's Pro early access request. */
  early_access: { status: EarlyAccessStatus | null }
}

export const billingSubscriptionQueryKey = (
  teamId?: string | number | null,
) => ['billing-subscription', teamId ?? null]

export default function useGetBillingSubscription({
  teamId,
  enabled = true,
  refetchOnMount,
}: {
  teamId?: string | number | null
  enabled?: boolean
  refetchOnMount?: boolean | 'always'
}) {
  // Set right before refetchForce() and read once by the next queryFn run —
  // refetch() itself always hits the network (bypassing React Query's own
  // cache), but the backend additionally caches this response for 24h per
  // team, so a plain refetch() can still come back stale. refresh_cache=1
  // busts that server-side cache too; only sent when explicitly forced, not
  // on every normal fetch, so we don't defeat the point of caching it.
  const forceRefresh = useRef(false)

  const query = useQuery<BillingSubscription>({
    queryKey: billingSubscriptionQueryKey(teamId),
    queryFn: async () => {
      const params = forceRefresh.current ? { refresh_cache: 1 } : undefined
      forceRefresh.current = false
      const response = await httpClient.get('billing/subscription', {
        headers: teamId ? { 'X-Team': String(teamId) } : undefined,
        params,
      })
      return response.data
    },
    enabled: enabled && !!teamId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount,
  })

  const refetchForce = () => {
    forceRefresh.current = true
    return query.refetch()
  }

  return { ...query, refetchForce }
}
